// paletteStore — module singleton that owns the active UI palette.
//
// NOT React state (CLAUDE.md hard rule): switching a palette writes CSS custom
// properties onto <html>, and the browser repaints. The graph hot path
// (BlueprintEditor / EventGraph / GraphEvaluator) never re-renders. UI surfaces
// that need to reflect the *selection* (the Appearance panel, the menu check)
// subscribe for a change tick, exactly like textureQuality.js.
//
// Persistence is per-user and LOCAL ONLY:
//   • scoped localStorage key (scopeKey('aos-palette')) — authoritative local
//     cache, namespaced by user id so two users on one browser don't collide.
//   • an unscoped boot-hint key — applied synchronously at import so a
//     non-default user doesn't flash the default theme before auth resolves.
//
// There is deliberately NO server sync. The original design (the 2026-07-21
// palette spec) planned a `/api/me/preferences` read/write as the cross-machine
// source of truth, and the client half shipped against it — but the endpoint was
// never built on any backend, so `/api/*` (convorcher's namespace) answered 404
// to a GET on every page load and a PUT on every palette change. Nothing ever
// came back, so the "server wins" reconcile branch never once executed; all it
// produced was a red line in every user's devtools. Removed rather than gated:
// keeping a call alive for a store that doesn't exist is code that can only
// fail. If cross-machine themes are wanted, build the backend first — the
// open-dependency note in the spec still stands.

import { scopeKey } from '../userScope'
import { DEFAULT_PALETTE_ID, getPalette } from './registry'
import { paletteToVars } from './tokens'

export const PALETTE_KEY_BASE = 'aos-palette'
// Unscoped boot hint — last palette applied on this browser, any user. Used
// only to avoid a first-paint flash; the scoped value + server reconcile it.
export const PALETTE_HINT_KEY = 'aos-palette-last'

const _state = { id: DEFAULT_PALETTE_ID, initialized: false }
const _listeners = new Set()

function _storage() {
  try { return window.localStorage } catch { return null }
}

function _notify() {
  for (const fn of _listeners) {
    try { fn() } catch (e) { console.warn('[palette] listener failed:', e) }
  }
}

export function subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// Write a palette's tokens onto the document root and tag it for CSS hooks
// (e.g. [data-theme="light"] overrides, scrollbar theming).
function applyVars(palette) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const vars = paletteToVars(palette)
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
  root.setAttribute('data-palette', palette.id)
  root.setAttribute('data-theme', palette.isLight ? 'light' : 'dark')
}

export function getPaletteId() { return _state.id }
export function getCurrentPalette() { return getPalette(_state.id) }

// Set the active palette: repaint + notify + persist (local + hint).
// `persist:false` applies without writing (used by the boot path, which is
// applying the value it just read out of storage).
export function setPalette(id, { persist = true } = {}) {
  const palette = getPalette(id)          // falls back to default on unknown id
  _state.id = palette.id
  applyVars(palette)
  _notify()
  if (persist) {
    const s = _storage()
    try { s?.setItem(scopeKey(PALETTE_KEY_BASE), palette.id) } catch { /* best-effort */ }
    try { s?.setItem(PALETTE_HINT_KEY, palette.id) } catch { /* best-effort */ }
  }
  return palette.id
}

// applyBootHint — synchronous, pre-auth. Applies the unscoped last-used palette
// so a returning non-default user doesn't flash the default theme. Safe to call
// at import; does nothing if no hint (tokens.css :root already provides the
// default values, so vars always resolve).
export function applyBootHint() {
  const hint = (() => { try { return _storage()?.getItem(PALETTE_HINT_KEY) } catch { return null } })()
  if (hint) applyVars(getPalette(hint))
}

// initPalette — post-auth resolve. scopeKey() now resolves to the real user id,
// so this is where the boot hint (any user on this browser) is replaced by THIS
// user's own stored choice. No stored value -> the default palette (a missing
// preference is normal, never an error). Idempotent.
export function initPalette() {
  if (_state.initialized) return _state.id
  _state.initialized = true

  const local = (() => { try { return _storage()?.getItem(scopeKey(PALETTE_KEY_BASE)) } catch { return null } })()
  setPalette(local || DEFAULT_PALETTE_ID, { persist: false })
  return _state.id
}

// Expose a small console/e2e handle. HMR-safe (window survives module reload).
if (typeof window !== 'undefined') {
  window.__PALETTE__ = {
    get id() { return _state.id },
    set: (id) => setPalette(id),
    get: () => getCurrentPalette(),
  }
}
